import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import type { SagaIterator } from 'redux-saga';
import type { Meta } from 'companion-app-components/flux/core/actionHelpers';
import type { BaseResource } from 'companion-app-components/flux/core/resourceStoreTypes';

import { resourceStoreTypes, resourceSagaUtils } from 'companion-app-components/flux/core';
import { getLogger, crashReporterInterface } from 'companion-app-components/utils/core';
import { subscribeForBumpUpdates } from 'companion-app-components/flux/bump';
import { featureFlagsSelectors } from 'companion-app-components/flux/feature-flags';
import { accountsUtils, accountsSelectors, accountsActions } from 'companion-app-components/flux/accounts';
import { tagsActions } from 'companion-app-components/flux/tags';
import { transactionsActions as actions, transactionsTransformers as transformers, transactionsUtils } from 'companion-app-components/flux/transactions';
import { chartOfAccountsUtils } from 'companion-app-components/flux/chart-of-accounts';
import { scheduledTransactionsActions } from 'companion-app-components/flux/scheduled-transactions';
import { preProcessUpdate } from 'companion-app-components/flux/transactions-processing/preProcess';

import { updatePayeeListSuccess } from 'data/payees/actions';
import { getPayeesForAccounts } from 'data/payees/selectors';
import { isUncategorizedTxn } from 'data/transactions/utils';
import * as transactionsActions from 'data/transactions/actions';
import { getTransactionById, getTransactionsByAccountId, getTransactionsById } from './selectors';

const log = getLogger('transactions/sagas');

// ====================================================
// AUTOCATEGORIZE TRANSACTIONS
//
// reviews recently retrieved transactions and looks for uncategorized transactions that have a match in the
// payee list, and will automatically categorize using the provided suggestion
//
export function* autoCategorizeTransactions(action): SagaIterator {
  const featureFlags = yield select(featureFlagsSelectors.getFeatureFlags);
  if (!featureFlags?.get('autoCategorizeUncategorized')) {
    return;
  }
  const { payload: txnsToCheck } = action;
  const payeeList = yield select(getPayeesForAccounts, { accountIds: null });

  let txnsToUpdate = txnsToCheck.map((txn) =>
    (isUncategorizedTxn(txn) && txn.payee && payeeList.get(txn.payee?.toLowerCase())) ? txn : null);
  // @ts-expect-error - FIXME: Expected 2 arguments, but got 1
  txnsToUpdate = txnsToUpdate.filter((txn) => (txn !== null && !accountsUtils.isLoanAccountFromId(txn.accountId)));
  txnsToUpdate = txnsToUpdate.map((txn) => {
    const payeeEntry = payeeList.get(txn.payee?.toLowerCase());
    if (payeeEntry && !transactionsUtils.isSplitTxn(payeeEntry.txn) && !transactionsUtils.isTransferTxn(payeeEntry.txn)) {
      return txn.set('coa', payeeEntry.txn.coa).set('split', payeeEntry.txn.split);
    }
    return null;
  });
  txnsToUpdate = txnsToUpdate.filter((txn) => txn !== null);

  // @ts-expect-error - FIXME: Expected at least 2 arguments, but got 1
  const txnsByAccountId = yield select(getTransactionsByAccountId);

  log.debug('Auto categorizing transactions pre ', txnsToUpdate);

  const txnPack = preProcessUpdate({
    txns: txnsToUpdate,
    txnsByAccountId,
    featureFlags,
  });

  log.debug('Auto categorizing transactions ', txnPack);

  if (txnPack.allTxnsToUpdate && txnPack.allTxnsToUpdate.length > 0) {
    yield put(actions.batchTransactions(txnPack.allTxnsToUpdate));
  }
}

// ====================================================
// UPDATE TRANSACTION CATEGORIES FOR PAYEE
//
// reviews recently retrieved transactions and looks for uncategorized transactions that have a match in the
// payee list, and will automatically categorize using the provided suggestion
export function* updateTransactionCategoriesForPayee(action): SagaIterator {
  const { coa, payee, priorCoa } = action.payload;
  // @ts-expect-error - FIXME: Expected 1 arguments, but got 2.
  const transactionsById = yield select(getTransactionsById, {});
  let txnsToUpdate = transactionsById.filter((txn) => (txn.payee === payee) && chartOfAccountsUtils.coasAreEqual(txn.coa, priorCoa));
  txnsToUpdate = txnsToUpdate.map((txn) => txn.set('coa', coa));
  // @ts-expect-error - FIXME: Expected at least 2 arguments, but got 1
  const txnsByAccountId = yield select(getTransactionsByAccountId);
  const featureFlags = yield select(featureFlagsSelectors.getFeatureFlags);
  const txnPack = preProcessUpdate({
    txns: txnsToUpdate,
    txnsByAccountId,
    featureFlags,
  });

  log.debug('Updating Transaction Categories by Payee ', txnPack);

  if (txnPack.allTxnsToUpdate && txnPack.allTxnsToUpdate.length > 0) {
    yield put(actions.batchTransactions(
      txnPack.allTxnsToUpdate,
      { context: 'register', undo: { userMessage: 'Transaction(s) updated.' } } as Meta<BaseResource, any>,
    ));
  }
}

//---------------------------------------------------
let lastGetTransactionsPayloads = [];

export function* storePayload(data): SagaIterator {
  log.debug('Store Payload', data);
  lastGetTransactionsPayloads = lastGetTransactionsPayloads.concat(data.payload.resources);
}

export function* triggerAutoCategorize() {
  if (lastGetTransactionsPayloads.length > 0) {
    yield put(transactionsActions.autoCategorizeTransactions(lastGetTransactionsPayloads));
    lastGetTransactionsPayloads = [];
  }
}
//---------------------------------------------------


// ====================================================
// PERFORM TRANSACTION ACTION
// a saga for utilizing the transactions/{id}/action or transactions/{id}/action/{otherId}
// pass in an action api object
//
// {
//   id: txnId to impact
//   action: 'ACCEPT' || 'SKIP' || 'MATCH' || 'UNMATCH'   See QCS Documentation for updates
//   otherId: if two IDs are involved in action (e.g. match)
// }
//
export function* performTransactionAction(actionData) {
  const config = resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceName: 'transaction action',
    resourceBaseUrl: (action) =>
      `/transactions/${action.payload.id}/${action.payload.action?.toLowerCase()}${action.payload.otherId ? `/${action.payload.otherId}` : ''}`,
    successAction: actions.performTransactionActionSuccess,
    failureAction: actions.performTransactionActionFailure,
    getLastSyncDate: () => null,
    transformResourceToRequestData: (resource) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, orderId, action, ...other } = resource;
      return { ...other };
    },
  });
  yield call(resourceSagaUtils.qcsSyncUpdateResource<any>, config, actionData);
}

// =================================================================================================
// Bump Updaters
// =================================================================================================

function* bumpDumpUpdater(resources) {
  const process = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpProcessTxnUpdates');
  if (process) {
    yield put(actions.applyTransactionsChanges(
      transformers.transformQcsTransactionsToTransactions(resources),
      { context: 'BUMP_CHANGE_DUMP' } as Meta<BaseResource, any>,
    ));
  }
}

function* bumpNudgeUpdater() {
  const process = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpProcessTxnUpdates');
  if (process) {
    yield put(actions.getTransactions(
      undefined,
      { context: 'BUMP_CHANGE_NUDGE' } as Meta<BaseResource, any>,
    ));
  }
}

subscribeForBumpUpdates('transactions',
  (resources) => bumpDumpUpdater(resources),
  () => bumpNudgeUpdater());

// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

function* ReportActionFailure(actionData) {
  // eslint-disable-next-line no-unsafe-optional-chaining
  const { response, request } = actionData?.payload;
  if (response && request) {
    if ((response.status === 400) && (/match/.exec(request?.responseURL)?.[0] === 'match')) {
      const firstId = /(\d+)(?=\/match\/)/.exec(request?.responseURL)?.[0];
      const secondId = /\/match\/(\d+)/.exec(request?.responseURL)?.[1];
      // @ts-expect-error - FIXME: Property 'set' does not exist on type 'SelectEffect'.ts(2339)
      const firstTxn = yield select(getTransactionById, firstId)?.set?.('memo', null);
      // @ts-expect-error - FIXME: Property 'set' does not exist on type 'SelectEffect'.ts(2339)
      const secondTxn = yield select(getTransactionById, secondId)?.set?.('memo', null);
      const firstAccount = yield select(accountsSelectors.getAccountById, firstTxn?.accountId);
      const secondAccount = yield select(accountsSelectors.getAccountById, secondTxn?.accountId);
      crashReporterInterface.reportError(Error('Match Failed! (details)'), (event) => event.addMetadata('custom', {
        description: 'ID is deleted?',
        txn1: firstTxn?.toJS?.() || null,
        txn2: secondTxn?.toJS?.() || null,
        account1: firstAccount?.toJS?.() || null,
        account2: secondAccount?.toJS?.() || null,
      }));
    }
  }
}
export function* transactionActionFailureWatcher(): SagaIterator {
  yield takeEvery(actions.performTransactionActionFailure, ReportActionFailure);
}

export function* getTransactionsSuccessActionWatcher(): SagaIterator {
  yield takeLatest([
    actions.getTransactionsSuccess,
  ], storePayload);
}

export function* updatePayeeListSuccessActionWatcher(): SagaIterator {
  yield takeLatest([
    updatePayeeListSuccess,
  ], triggerAutoCategorize);
}

export function* autoCategorizeTransactionsActionWatcher(): SagaIterator {
  yield takeLatest(transactionsActions.autoCategorizeTransactions, autoCategorizeTransactions);
}

export function* updateTransactionCategoriesForPayeeActionWatcher(): SagaIterator {
  yield takeLatest(transactionsActions.updateTransactionCategoriesForPayeeAction, updateTransactionCategoriesForPayee);
}

export function* deleteTagActionWatcher(): SagaIterator {
  yield takeEvery(tagsActions.deleteTag, function* deleteTag(action) {
    // @ts-expect-error - Property 'txns' does not exist on type 'Meta<BaseResource, any>'
    if (action.meta?.txns?.size) {
      // @ts-expect-error - Property 'txns' does not exist on type 'Meta<BaseResource, any>'
      yield put(actions.batchTransactions(action.meta?.txns));
    }
  });
}

// check if the action was for a schedule txns instance get ScheduleTxn for QCS
function* handleSchTransPending(load): SagaIterator {
  if (load?.payload && load.payload[0]?.stModelId) {
    yield put(scheduledTransactionsActions.getScheduledTransactions());
  }
}

// action watcher to handle action on sechduled transaction instance(Transaction)
export function* getTransactionsActionSuccessWatcher(): SagaIterator {
  yield takeLatest(actions.performTransactionActionSuccess, handleSchTransPending);
}


export function* getAccountOnUpdateTransactionSuccess(action) {
  const { payload } = action;
  const hasMissingBalance = Array.isArray(payload)
    ? payload.some((txn) => !txn.balance)
    : !payload?.balance;

  if (hasMissingBalance) {
    yield put(accountsActions.getAccounts());
  }
}

export function* updateTxnActionWatcher() {
  yield takeEvery([actions.updateTransactionSuccess, actions.batchTransactionsSuccess], getAccountOnUpdateTransactionSuccess);
}

// ====================================================
// EXPORTS

export default [
  autoCategorizeTransactionsActionWatcher,
  updatePayeeListSuccessActionWatcher,
  updateTransactionCategoriesForPayeeActionWatcher,
  getTransactionsSuccessActionWatcher,
  getTransactionsActionSuccessWatcher,
  deleteTagActionWatcher,
  transactionActionFailureWatcher,
  updateTxnActionWatcher,
];
